import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DASHBOARD_COLORS, getEnergyColor } from '../../config/colorScheme';

// Import SVG icons
import { ReactComponent as OffshoreWindIcon } from '../assets/icons/icon_offshore-wind.svg';
import { ReactComponent as SolarIcon } from '../assets/icons/icon_solar.svg';
import { ReactComponent as TidalIcon } from '../assets/icons/icon_tidal.svg';
import { ReactComponent as HydroIcon } from '../assets/icons/icon_tidal-3.svg';
import { ReactComponent as BiomassIcon } from '../assets/icons/icon_biomass.svg';
import { ReactComponent as NuclearIcon } from '../assets/icons/icon_nuclear-plant.svg';

// Icon components that resize properly
const createIconComponent = (IconSvg) => ({ size = 24, ...props }) => (
  <IconSvg
    width={size}
    height={size}
    style={{ maxWidth: '100%', maxHeight: '100%' }}
    {...props}
  />
);

// Updated IconMap with proper energy source icons
const IconMap = {
  'Offshore Wind Farms': createIconComponent(OffshoreWindIcon),
  'Solar Farms': createIconComponent(SolarIcon),
  'Tidal Energy Projects': createIconComponent(TidalIcon),
  'Hydropower Plants': createIconComponent(HydroIcon),
  'Biomass Power Stations': createIconComponent(BiomassIcon),
  'Nuclear Micro Plants': createIconComponent(NuclearIcon),
  // Fallback icon for any unmapped sources
  Info: () => <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
};

const PowerGenerationLandUsage = ({ powerGenerationNeeds }) => {
  const [selectedSource, setSelectedSource] = useState(null);

  // Energy source data with land usage details
  const energySources = Object.entries(powerGenerationNeeds)
    .filter(([key]) => key !== 'totalInvestment' && key !== 'totalLandArea' && key !== 'totalCapacityMW')
    .map(([key, details]) => {
      const displayName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      const sourceDetails = {
        name: displayName,
        icon: IconMap[displayName] || IconMap.Info,

        projects: details.count,
        power: details.details.capacityMW,
        landUsage: (details.details.landArea || 0) * 0.386102, // Convert km² to square miles
        color: (() => {
          const techMap = {
            'Offshore Wind Farms': 'offshoreWind',
            'Solar Farms': 'solar',
            'Tidal Energy Projects': 'tidal',
            'Hydropower Plants': 'hydro',
            'Biomass Power Stations': 'biomass',
            'Nuclear Micro Plants': 'nuclear'
          };
          const tech = techMap[displayName];
          return tech ? getEnergyColor(tech, 'primary') : DASHBOARD_COLORS.neutral.baseline.primary;
        })(),

        description: {
          'Offshore Wind Farms': 'Located in coastal waters, no land footprint',
          'Solar Farms': 'Large-scale ground-mounted solar installations',
          'Tidal Energy Projects': 'Underwater turbines in tidal channels',
          'Hydropower Plants': 'Dams and reservoirs for hydroelectric generation',
          'Biomass Power Stations': 'Power stations burning organic materials',
          'Nuclear Micro Plants': 'Small modular reactors with minimal footprint'
        }[displayName] || 'Renewable energy source',

        comparison: {
          'Offshore Wind Farms': 'Uses sea area instead of land',
          'Solar Farms': 'Uses ground area for panel installation',
          'Tidal Energy Projects': 'Minimal land, mostly underwater',
          'Hydropower Plants': 'Includes reservoir and dam area',
          'Biomass Power Stations': 'Compact power generation facilities',
          'Nuclear Micro Plants': 'Extremely compact land usage'
        }[displayName] || 'Land usage comparison'
      };
      return sourceDetails;
    });

  // Total land usage calculation
  const totalLandUsage = energySources.reduce((sum, source) => sum + source.landUsage, 0);
  const totalPower = energySources.reduce((sum, source) => sum + source.power, 0);

  // Manchester city area reference (approximately 493 square miles)
  const manchesterArea = 492.6; // 1,276 km² converted to square miles

  const SourceCard = ({ source, isSelected, onClick }) => {
    const Icon = source.icon;
    return (
      <div
        className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 ${
          isSelected
            ? 'border-blue-500 bg-blue-50 shadow-md'
            : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
        }`}
        onClick={onClick}
      >
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg`} style={{ backgroundColor: source.color + '20' }}>
            <Icon />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{source.name}</h3>
            <div className="mt-2 flex justify-between items-center">
              <span className="text-lg font-bold" style={{ color: source.color }}>
                {source.power} MW
              </span>
              <span className="text-sm text-gray-500">
                {Math.round((source.power / totalPower) * 100)}% of total
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Land: {source.landUsage.toFixed(1)} sq mi
            </p>
          </div>
        </div>
      </div>
    );
  };

  const ManchesterMap = () => {
    const getCircleSize = (landUsage) => {
      const proportion = landUsage / manchesterArea;
      const baseSize = Math.sqrt(proportion) * 100;
      return Math.max(baseSize, 3); // Minimum size for visibility
    };

    const getSourcePosition = (index) => {
      const positions = [
        { x: 150, y: 100 },   // Top left
        { x: 450, y: 100 },   // Top right
        { x: 300, y: 200 },   // Center
        { x: 180, y: 300 },   // Bottom left
        { x: 420, y: 300 },   // Bottom right
        { x: 300, y: 350 }    // Bottom center (for 6th item if needed)
      ];
      return positions[index] || { x: 300, y: 200 };
    };

    return (
      <div className="bg-white rounded-lg p-4">

        <p className="text-sm text-gray-600 mb-4">
          Greater Manchester covers 493 square miles. Here's how renewable energy land usage compares to the entire county area:
        </p>

        <div className="flex flex-col lg:flex-row gap-6">
          <div
            className="flex-1 max-w-full rounded-lg p-4"
            style={{
              backgroundImage: 'url(/manchester-district-map.png)',
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              backgroundBlendMode: 'overlay',
              opacity: 0.9
            }}
          >
            <svg
              viewBox="0 0 600 400"
              preserveAspectRatio="xMidYMid meet"
              className="w-full h-auto min-h-[300px] max-h-[500px] bg-transparent"
            >
              <text x="300" y="20" textAnchor="middle" className="text-lg font-semibold" fill="#374151">
                Greater Manchester District Map (493 sq mi)
              </text>

              {/* Energy source land usage circles */}
              {energySources
                .filter(source => source.landUsage > 0)
                .map((source, index) => {
                  const position = getSourcePosition(index);
                  const size = getCircleSize(source.landUsage) * 1.5; // Increased size
                  const Icon = source.icon;
                  return (
                    <g key={source.name}>
                      {/* Outer colored circle */}
                      <circle
                        cx={position.x}
                        cy={position.y}
                        r={size}
                        fill={source.color}
                        fillOpacity="0.7"
                        stroke={source.color}
                        strokeWidth="2"
                      />
                      {/* Inner white circle for icon background */}
                      <circle
                        cx={position.x}
                        cy={position.y}
                        r={size * 0.7}
                        fill="white"
                        fillOpacity="0.95"
                        stroke={source.color}
                        strokeWidth="1"
                      />
                      <foreignObject
                        x={position.x - (size * 0.7)/2}
                        y={position.y - (size * 0.7)/2}
                        width={size * 0.7}
                        height={size * 0.7}
                        className="flex items-center justify-center"
                      >
                        <div
                          className="flex items-center justify-center w-full h-full"
                          style={{ color: source.color, width: `${size * 0.7}px`, height: `${size * 0.7}px` }}
                        >
                          <Icon size={size * 0.55} />
                        </div>
                      </foreignObject>
                    </g>
                  );
                })}

              {/* Legend */}
              <text x="200" y="398" className="text-sm" fill="#6b7280">
                Circle size proportional to land usage (sq mi)
              </text>
            </svg>
          </div>

          <div className="lg:w-80">
            <h3 className="font-semibold text-gray-900 mb-3">Scale Perspective</h3>
            <div className="space-y-3">
              {energySources
                .filter(source => source.landUsage > 0)
                .sort((a, b) => b.landUsage - a.landUsage)
                .map((source) => {
                  const percentage = ((source.landUsage / manchesterArea) * 100).toFixed(1);
                  const Icon = source.icon;
                  return (
                    <div key={source.name} className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: source.color }}
                      />
                      <div className="w-5 h-5 flex items-center justify-center">
                        <Icon />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {source.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {source.landUsage.toFixed(1)} sq mi ({percentage}% of Manchester)
                        </div>
                      </div>
                    </div>
                  );
                })}

              <div className="pt-2 border-t">
                <div className="text-sm font-medium text-gray-900">
                  Total Land Usage: {totalLandUsage.toFixed(1)} sq mi
                </div>
                <div className="text-xs text-gray-500">
                  {((totalLandUsage / manchesterArea) * 100).toFixed(1)}% of Manchester's area
                </div>
              </div>

              <div className="pt-2 bg-blue-50 p-3 rounded-lg">
                <div className="text-sm font-medium text-blue-900">
                  💡 Key Insight
                </div>
                <div className="text-xs text-blue-800 mt-1">
                  All renewable energy land usage combined equals just {((totalLandUsage / manchesterArea) * 100).toFixed(1)}% of Greater Manchester's total area, yet generates {totalPower} MW of clean power!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">

      {/* Manchester Map Comparison */}
      {/* <ManchesterMap /> */}

    </div>
  );
};

export default PowerGenerationLandUsage;
